import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Briefcase, Award, Users, GraduationCap, Target, TrendingUp, Globe, CheckCircle, Star, Quote, Sparkles, Zap, Heart, Play, ChevronRight, MapPin, Mail, Phone, Youtube, ExternalLink } from 'lucide-react';
import { personalInfo, achievements, expertise, experience, projects, mediaAppearances, socialLinks } from '../data/mock';
import { useInView } from '../hooks/useInView';

const HomePage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ threshold: 0.1 });
  const [aboutRef, aboutInView] = useInView({ threshold: 0.1 });
  const [expertiseRef, expertiseInView] = useInView({ threshold: 0.1 });
  const [journeyRef, journeyInView] = useInView({ threshold: 0.1 });
  const [impactRef, impactInView] = useInView({ threshold: 0.1 });
  const [galleryRef, galleryInView] = useInView({ threshold: 0.1 });
  const [mediaRef, mediaInView] = useInView({ threshold: 0.1 });
  const [ctaRef, ctaInView] = useInView({ threshold: 0.1 });

  const impactStats = [
    { icon: Users, number: "41,000+", label: "Staff Managed", gradient: "from-emerald-400 to-green-600", shadow: "shadow-emerald-500/30" },
    { icon: GraduationCap, number: "70+", label: "Theses Supervised", gradient: "from-amber-400 to-orange-500", shadow: "shadow-amber-500/30" },
    { icon: BookOpen, number: "40+", label: "Postgrad Classes", gradient: "from-blue-400 to-indigo-600", shadow: "shadow-blue-500/30" },
    { icon: Target, number: "$3.75M+", label: "Budget Managed", gradient: "from-purple-400 to-violet-600", shadow: "shadow-purple-500/30" },
  ];

  const galleryImages = [
    { src: "/images/IMG_0337.jpg", title: "Conference Speaker" },
    { src: "/images/IMG_8899.png", title: "Academic Leadership" },
    { src: "/images/18-56-08.jpg", title: "Director General" },
    { src: "/images/IMG_0004.jpg", title: "Research Workshop" },
    { src: "/images/IMG_0011.jpg", title: "Electoral Commission" },
    { src: "/images/18-56-09.jpg", title: "Training Session" },
  ];

  const expertiseColors = [
    "from-emerald-400 to-green-600",
    "from-amber-400 to-orange-500",
    "from-blue-400 to-indigo-600",
    "from-purple-400 to-violet-600",
    "from-rose-400 to-pink-600",
    "from-cyan-400 to-teal-600",
    "from-red-400 to-orange-500",
    "from-indigo-400 to-purple-600",
  ];

  return (
    <>
      {/* Hero Section - Full Screen Immersive */}
      <section className="min-h-screen relative overflow-hidden" ref={heroRef}>
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="/images/hero-new.jpg" 
            alt="Dr. Zaki" 
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F3C88]/90 via-[#1F3C88]/70 to-[#1F3C88]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F3C88]/60 via-transparent to-transparent" />
        </div>
        
        {/* Animated Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(25)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float"
              style={{
                width: `${4 + Math.random() * 8}px`,
                height: `${4 + Math.random() * 8}px`,
                background: i % 2 === 0 ? '#C9A24D' : '#ffffff',
                opacity: 0.2 + Math.random() * 0.3,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${4 + Math.random() * 6}s`
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className={`inline-flex items-center gap-3 bg-gradient-to-r from-emerald-400 to-green-600 text-white px-6 py-3 rounded-full mb-8 shadow-xl shadow-emerald-500/30 transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Sparkles className="w-5 h-5" />
                <span className="font-bold">PhD in Sociology</span>
                <span className="w-px h-5 bg-white/30" />
                <span className="font-bold">Honor Graduate</span>
              </div>

              {/* Name */}
              <h1 className={`text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white mb-6 leading-[0.95] transition-all duration-1000 delay-200 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="block">Dr. Zakariye</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300 animate-gradient">
                  Abdi Bade
                </span>
              </h1>

              {/* Role Tags */}
              <div className={`flex flex-wrap gap-3 mb-6 transition-all duration-1000 delay-300 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                {['Sociologist', 'Academic Leader', 'Researcher', 'Consultant'].map((role, index) => (
                  <span 
                    key={role} 
                    className={`px-4 py-2 rounded-full text-white font-bold text-sm bg-gradient-to-r ${expertiseColors[index]} shadow-lg`}
                  >
                    {role}
                  </span>
                ))}
              </div>

              {/* Tagline */}
              <p className={`text-xl text-white/80 max-w-xl mb-10 leading-relaxed font-medium transition-all duration-1000 delay-400 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                Advancing sociological research, governance, and institutional development in Somaliland and beyond.
              </p>

              {/* CTA Buttons */}
              <div className={`flex flex-wrap gap-4 mb-16 transition-all duration-1000 delay-500 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Link 
                  to="/about" 
                  className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-full shadow-xl shadow-amber-500/30 hover:scale-105 hover:-translate-y-1 transition-all duration-300"
                >
                  <span>Discover My Journey</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/contact" 
                  className="group flex items-center gap-3 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300"
                >
                  <span>Get in Touch</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Mini Stats */}
              <div className={`grid grid-cols-4 gap-4 transition-all duration-1000 delay-600 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                {achievements.map((stat, index) => (
                  <div key={index} className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all duration-300">
                    <p className="text-3xl lg:text-4xl font-black text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.number}</p>
                    <p className="text-white/70 text-xs font-semibold mt-1 uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/40 rounded-full flex justify-center pt-2">
            <div className="w-2 h-4 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section - Compact Professional Cards */}
      <section className="py-14 bg-white relative" ref={statsRef}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {/* Section Header - Compact */}
          <div className={`text-center mb-10 transition-all duration-700 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-emerald-400 to-green-600 text-white text-xs font-black rounded-full mb-4 shadow-lg shadow-emerald-500/30 uppercase tracking-wider">
              Career Highlights
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-[#1C1C1C]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Impact & <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">Achievements</span>
            </h2>
          </div>
          
          {/* Stats Grid - Smaller Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {impactStats.map((stat, index) => (
              <div
                key={index}
                className={`group relative rounded-2xl p-5 bg-gradient-to-br ${stat.gradient} overflow-hidden transition-all duration-500 hover:scale-105 hover:-translate-y-2 ${stat.shadow} shadow-xl cursor-pointer ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                
                {/* Content */}
                <div className="relative z-10 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl lg:text-3xl font-black text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {stat.number}
                    </p>
                    <p className="text-white/80 text-xs font-semibold">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white" ref={aboutRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            {/* Image Collage */}
            <div className={`relative transition-all duration-1000 ${aboutInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-5">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                    <img src="/images/IMG_8899.png" alt="Profile" className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-600/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                    <img src="/images/18-56-08.jpg" alt="At Work" className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-600/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
                <div className="space-y-5 pt-10">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                    <img src="/images/IMG_0337.jpg" alt="Conference" className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-amber-600/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                    <img src="/images/18-56-09.jpg" alt="Training" className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-600/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-400 to-green-600 text-white rounded-3xl px-8 py-5 shadow-2xl shadow-emerald-500/30">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-4xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>10+</p>
                    <p className="text-xs font-bold text-white/80 uppercase">Years</p>
                  </div>
                  <div className="w-px h-14 bg-white/30" />
                  <div className="text-center">
                    <p className="text-4xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>PhD</p>
                    <p className="text-xs font-bold text-white/80 uppercase">Sociology</p>
                  </div>
                  <div className="w-px h-14 bg-white/30" />
                  <div className="text-center">
                    <p className="text-4xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>4</p>
                    <p className="text-xs font-bold text-white/80 uppercase">Languages</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`transition-all duration-1000 delay-300 ${aboutInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <span className="inline-block px-6 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-black rounded-full mb-6 shadow-xl shadow-amber-500/30 uppercase tracking-wider">
                About Me
              </span>
              <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-6 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Academic Leader & <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">Research Expert</span>
              </h2>
              
              <p className="text-[#4A5568] text-lg leading-relaxed mb-10 font-medium">
                Dr. Zakariye Abdi Bade is an accomplished academic leader with over 10 years of experience in higher education, research, consultancy, and institutional leadership. His expertise spans governance, state-building, social marginalization, and educational development in the Horn of Africa.
              </p>
              
              {/* Feature Cards */}
              <div className="grid grid-cols-2 gap-4 mb-10">
                {[
                  { icon: Award, text: "Honor Graduate", subtitle: "With Distinction", gradient: "from-amber-400 to-orange-500", shadow: "shadow-amber-500/30" },
                  { icon: Globe, text: "4 Languages", subtitle: "Multilingual", gradient: "from-emerald-400 to-green-600", shadow: "shadow-emerald-500/30" },
                  { icon: BookOpen, text: "12+ Publications", subtitle: "Research Papers", gradient: "from-blue-400 to-indigo-600", shadow: "shadow-blue-500/30" },
                  { icon: TrendingUp, text: "15+ Projects", subtitle: "Successfully Led", gradient: "from-purple-400 to-violet-600", shadow: "shadow-purple-500/30" },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className={`group relative rounded-2xl p-5 bg-gradient-to-br ${item.gradient} overflow-hidden cursor-pointer transition-all duration-500 hover:scale-105 hover:-translate-y-1 shadow-xl ${item.shadow}`}
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
                    <div className="relative z-10 flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                        <item.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-black text-white text-lg" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.text}</h4>
                        <p className="text-white/80 text-sm font-medium">{item.subtitle}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Link to="/about" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#1F3C88] to-[#2A4FA8] text-white font-bold rounded-full shadow-xl shadow-[#1F3C88]/30 hover:scale-105 hover:-translate-y-1 transition-all duration-300 group">
                Learn More About Me
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-24 bg-white" ref={expertiseRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${expertiseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-6 py-2.5 bg-gradient-to-r from-purple-400 to-violet-600 text-white text-sm font-black rounded-full mb-6 shadow-xl shadow-purple-500/30 uppercase tracking-wider">
              Expertise
            </span>
            <h2 className="text-4xl lg:text-6xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Areas of <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-violet-600">Specialization</span>
            </h2>
            <p className="text-[#718096] text-lg max-w-2xl mx-auto font-medium">
              Combining academic rigor with practical expertise across multiple domains
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.slice(0, 8).map((item, index) => (
              <div
                key={item.id}
                className={`group rounded-3xl p-8 bg-gradient-to-br ${expertiseColors[index]} overflow-hidden transition-all duration-500 hover:scale-105 hover:-translate-y-3 shadow-xl ${expertiseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <span className="text-2xl font-black text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="text-white font-black text-lg mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed font-medium">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Journey */}
      <section className="py-24 bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] text-white relative overflow-hidden" ref={journeyRef}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className={`transition-all duration-700 ${journeyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span className="inline-block px-6 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-black rounded-full mb-6 shadow-xl shadow-amber-500/30 uppercase tracking-wider">
                Career
              </span>
              <h2 className="text-4xl lg:text-6xl font-black mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Professional <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">Journey</span>
              </h2>
              
              <div className="space-y-6">
                {experience.slice(0, 4).map((exp, index) => {
                  const colors = ['from-emerald-400 to-green-600', 'from-amber-400 to-orange-500', 'from-blue-400 to-indigo-600', 'from-purple-400 to-violet-600'];
                  return (
                    <div 
                      key={exp.id} 
                      className={`flex gap-5 transition-all duration-700 ${journeyInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                      style={{ transitionDelay: `${index * 150 + 300}ms` }}
                    >
                      <div className="relative flex-shrink-0">
                        <div className={`w-14 h-14 bg-gradient-to-br ${colors[index]} rounded-2xl flex items-center justify-center shadow-xl`}>
                          <Briefcase className="w-7 h-7 text-white" />
                        </div>
                        {index < 3 && <div className="absolute top-16 left-1/2 w-0.5 h-10 bg-white/20 -translate-x-1/2" />}
                      </div>
                      <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-white/10 transition-colors duration-300">
                        <span className={`inline-block px-3 py-1 bg-gradient-to-r ${colors[index]} text-white text-xs font-bold rounded-full mb-2`}>{exp.period}</span>
                        <h4 className="text-white font-black text-lg" style={{ fontFamily: "'Montserrat', sans-serif" }}>{exp.role}</h4>
                        <p className="text-white/70 font-medium">{exp.organization}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Link to="/experience" className="inline-flex items-center gap-3 mt-10 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-full shadow-xl shadow-amber-500/30 hover:scale-105 hover:-translate-y-1 transition-all duration-300 group">
                View Complete Timeline
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className={`relative transition-all duration-700 delay-500 ${journeyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/IMG_0337.jpg"
                  alt="Dr. Zaki at Conference"
                  className="w-full h-[550px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F3C88] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-10">
                  <Quote className="w-12 h-12 text-amber-400 mb-4" />
                  <p className="text-white text-xl font-bold italic leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    "Committed to evidence-based decision-making and empowering communities through education."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Strip */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden" ref={galleryRef}>
        <div className={`transition-all duration-700 ${galleryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-14">
            <span className="inline-block px-6 py-2.5 bg-gradient-to-r from-rose-400 to-pink-600 text-white text-sm font-black rounded-full mb-6 shadow-xl shadow-rose-500/30 uppercase tracking-wider">
              Gallery
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Moments of <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-pink-600">Impact</span>
            </h2>
          </div>
          
          {/* Scrolling Gallery */}
          <div className="flex gap-6 animate-scroll">
            {[...galleryImages, ...galleryImages].map((img, index) => {
              const colors = ['from-emerald-400 to-green-600', 'from-amber-400 to-orange-500', 'from-blue-400 to-indigo-600', 'from-purple-400 to-violet-600', 'from-rose-400 to-pink-600', 'from-cyan-400 to-teal-600'];
              return (
                <div key={index} className="flex-shrink-0 w-80 h-64 rounded-3xl overflow-hidden shadow-xl group relative">
                  <img 
                    src={img.src} 
                    alt={img.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${colors[index % colors.length]} opacity-0 group-hover:opacity-80 transition-opacity duration-500 flex items-end justify-center pb-6`}>
                    <span className="text-white font-bold text-lg">{img.title}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-white" ref={impactRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className={`transition-all duration-700 ${impactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span className="inline-block px-6 py-2.5 bg-gradient-to-r from-cyan-400 to-teal-600 text-white text-sm font-black rounded-full mb-6 shadow-xl shadow-cyan-500/30 uppercase tracking-wider">
                Impact
              </span>
              <h2 className="text-4xl lg:text-6xl font-black text-[#1C1C1C] mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Making a <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-teal-600">Difference</span>
              </h2>
              <p className="text-[#4A5568] text-lg leading-relaxed mb-10 font-medium">
                Through research, education, and institutional leadership, I've contributed to governance, electoral processes, and community development across Somaliland and the Horn of Africa.
              </p>
              
              <div className="space-y-4">
                {[
                  { text: "Led electoral operations managing 18,000+ staff", gradient: "from-emerald-400 to-green-600", shadow: "shadow-emerald-500/30" },
                  { text: "Published 12+ peer-reviewed research papers", gradient: "from-amber-400 to-orange-500", shadow: "shadow-amber-500/30" },
                  { text: "Taught at 4 major universities", gradient: "from-blue-400 to-indigo-600", shadow: "shadow-blue-500/30" },
                  { text: "Managed budgets exceeding $3.75 million", gradient: "from-purple-400 to-violet-600", shadow: "shadow-purple-500/30" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4 group">
                    <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center shadow-lg ${item.shadow} group-hover:scale-110 transition-transform duration-300`}>
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[#1C1C1C] font-semibold text-lg">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={`grid grid-cols-2 gap-5 transition-all duration-700 delay-300 ${impactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {projects.slice(0, 4).map((project, index) => {
                const colors = ['from-emerald-400 to-green-600', 'from-amber-400 to-orange-500', 'from-blue-400 to-indigo-600', 'from-purple-400 to-violet-600'];
                return (
                  <div key={project.id} className="relative rounded-3xl overflow-hidden shadow-xl group">
                    <img src={project.image} alt={project.title} className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className={`absolute inset-0 bg-gradient-to-t ${colors[index]} opacity-80 group-hover:opacity-90 transition-opacity duration-500`} />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <p className="text-white font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>{project.title}</p>
                      <p className="text-white/80 text-sm font-medium">{project.client}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Media & YouTube Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" ref={mediaRef}>
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-red-200/30 to-pink-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-gradient-to-br from-orange-200/30 to-amber-200/30 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className={`text-center mb-16 transition-all duration-700 ${mediaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm font-black rounded-full mb-6 shadow-lg shadow-red-500/30">
              <Youtube className="w-4 h-4" />
              MEDIA APPEARANCES
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Watch & <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-pink-500">Learn</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Educational content on research methodologies and academic excellence
            </p>
          </div>

          {/* YouTube Videos Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {mediaAppearances.map((video, index) => (
              <div 
                key={video.id}
                className={`group bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 ${mediaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Video Embed */}
                <div className="relative aspect-video bg-gray-900">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                {/* Video Info */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">
                      {video.platform}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-red-500 transition-colors line-clamp-2">
                    {video.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          {/* YouTube Channel CTA */}
          <div className={`text-center transition-all duration-700 delay-300 ${mediaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <a 
              href={socialLinks.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold rounded-full shadow-xl shadow-red-500/30 hover:scale-105 hover:-translate-y-1 transition-all duration-300 group"
            >
              <Youtube className="w-6 h-6" />
              Subscribe to YouTube Channel
              <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-28 bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] relative overflow-hidden" ref={ctaRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <div className={`transition-all duration-700 ${ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-6 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-black rounded-full mb-8 shadow-xl shadow-amber-500/30 uppercase tracking-wider">
              Open for Collaboration
            </span>
            <h2 className="text-4xl lg:text-7xl font-black text-white mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Let's Work <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">Together</span>
            </h2>
            <p className="text-white/80 text-xl mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
              Whether you need research consultation, academic supervision, or institutional capacity building, I'm here to help.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-4 px-12 py-6 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-black text-lg rounded-full shadow-2xl shadow-amber-500/40 hover:scale-105 hover:-translate-y-2 transition-all duration-300 group">
              Get in Touch
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
